import React from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@core/components/ui/switch";
import { Input } from "@core/components/ui/input";
import type { SettingDescriptor } from "./types";
import SettingHint from "./SettingHint";
import type { Hint } from "./useSettingsHints";

interface SettingRowProps {
  setting: SettingDescriptor;
  /** Si fourni, click "Modifier"/"Ajouter" propage. */
  onEditRequest?: (key: SettingDescriptor["key"]) => void;
  /** Row actuellement expanded — rend `editor` inline si non vide */
  expanded?: boolean;
  /** Hint actif pour cette row (PR 2) — affiché en bandeau jaune sous la value */
  hint?: Hint | null;
  /** Click sur "Utiliser « X » →" dans le bandeau hint */
  onApplySuggestion?: (key: Hint["key"], value: unknown) => void;
  /** Editeur inline à rendre quand `expanded === true` (PR 3) */
  editor?: React.ReactNode;
  /** Commit direct (settings booléens) — rend un Switch inline à la place du bouton "Modifier" */
  onCommit?: (key: SettingDescriptor["key"], value: unknown) => void;
}

/**
 * Une row du PrePublishModal. Affiche : icon box + label/value/sub-desc + bouton.
 * En PR 1, le bouton est read-only (pas de toggle d'édition).
 * Cf. Pre-publish Modal B.html lignes 178-269.
 */
const SettingRow: React.FC<SettingRowProps> = ({
  setting,
  onEditRequest,
  expanded = false,
  hint,
  onApplySuggestion,
  editor,
  onCommit,
}) => {
  const { t } = useTranslation("polls");
  const isHint = setting.group === "hint";
  const isInlineBoolean = setting.type === "boolean" && !!onCommit;
  const isInlineString =
    setting.type === "string" && setting.alwaysOpen && !!onCommit;
  const isImportant = setting.group === "important";
  const buttonLabel = setting.isEmpty
    ? t("prepublish_btn_add")
    : expanded
      ? t("prepublish_btn_close")
      : t("prepublish_btn_edit");

  return (
    <div
      data-testid={`setting-row-${setting.key}`}
      data-empty={setting.isEmpty ? "true" : undefined}
      data-expanded={expanded ? "true" : undefined}
      className="px-1 py-1.5"
    >
      <div className="flex items-start gap-3">
        {/* Icon box */}
        <div
          aria-hidden
          className={`flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-[8px] text-[14px] ${
            isHint ? "bg-warning/[0.18]" : "bg-card"
          }`}
        >
          {setting.icon}
        </div>

        {/* Label + Value + Sub-description */}
        <div className="flex-1 min-w-0">
          <div className="text-[11px] tracking-[0.3px] text-muted-foreground">
            {setting.label}
          </div>
          {isInlineString ? (
            <InlineStringField setting={setting} onCommit={onCommit!} />
          ) : (
            <>
              <div className="mt-[1px] text-[13px] leading-snug">
                <span
                  className={
                    setting.isEmpty
                      ? "font-medium text-muted-foreground"
                      : "font-semibold text-foreground"
                  }
                >
                  {setting.displayValue ??
                    setting.value ??
                    t("prepublish_unspecified")}
                </span>
                {setting.isDefault && !setting.isEmpty && isImportant && (
                  <span
                    data-testid={`setting-row-${setting.key}-default-badge`}
                    className="ml-1.5 text-[9px] font-medium text-muted-foreground/40"
                  >
                    · {t("prepublish_default_badge")}
                  </span>
                )}
              </div>
              {setting.subDescription && (
                <div className="mt-[3px] text-[11px] text-muted-foreground/40">
                  {setting.subDescription}
                </div>
              )}
            </>
          )}
        </div>

        {/* Switch inline pour les booléens — pas d'accordéon "Modifier" */}
        {isInlineBoolean ? (
          <Switch
            checked={setting.value === "true"}
            onCheckedChange={(v) => onCommit?.(setting.key, v)}
            data-testid={`setting-row-${setting.key}-switch`}
            aria-label={setting.label}
          />
        ) : isInlineString ? null : (
          <button
            type="button"
            data-testid={`setting-row-${setting.key}-button`}
            onClick={() => onEditRequest?.(setting.key)}
            className={
              isHint
                ? "h-8 rounded-md border border-warning bg-warning px-3 text-[12px] font-semibold text-warning-foreground transition-colors hover:bg-warning/90"
                : "h-8 rounded-md border border-primary/40 bg-transparent px-3 text-[12px] font-semibold text-primary transition-colors hover:bg-primary/10"
            }
          >
            {buttonLabel}
          </button>
        )}
      </div>
      {/* Champ imbriqué (ex: adresse de copie sous "Notifications email") —
          même carte que le parent, visible seulement switch actif. Groupés
          car c'est une seule décision côté invité, pas deux réglages
          indépendants (feedback 2026-08-17). */}
      {setting.nested && setting.value === "true" && onCommit && (
        <div className="ml-[42px] mt-1.5">
          <div className="text-[11px] tracking-[0.3px] text-muted-foreground">
            {setting.nested.label}
          </div>
          <InlineStringField setting={setting.nested} onCommit={onCommit} />
          {setting.nested.subDescription && (
            <div className="mt-[3px] text-[11px] text-muted-foreground/40">
              {setting.nested.subDescription}
            </div>
          )}
        </div>
      )}
      {hint && !expanded && (
        <SettingHint hint={hint} onApplySuggestion={onApplySuggestion} />
      )}
      {expanded && editor}
    </div>
  );
};

/**
 * String toujours en édition — pas d'accordéon, commit onBlur/Enter.
 * Pas d'autoFocus : le champ (ex. « Votre email » invité, facultatif) est en
 * bas de la modale ; le focus à l'ouverture la faisait défiler jusqu'à lui,
 * titre hors champ à 320 px, et l'anneau de focus le montrait en « erreur »
 * alors que rien n'est requis (#1482).
 */
const InlineStringField: React.FC<{
  setting: SettingDescriptor;
  onCommit: (key: SettingDescriptor["key"], value: unknown) => void;
}> = ({ setting, onCommit }) => {
  const [value, setValue] = React.useState(setting.value ?? "");
  React.useEffect(() => {
    setValue(setting.value ?? "");
  }, [setting.value]);
  const commit = () => {
    if (value !== (setting.value ?? "")) onCommit(setting.key, value.trim());
  };
  return (
    <Input
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        }
      }}
      data-testid={`setting-row-${setting.key}-inline-input`}
      placeholder={setting.displayValue ?? ""}
      className="mt-1 h-8 text-[13px]"
    />
  );
};

export default SettingRow;
